import { useMutation } from '@apollo/client/react';
import { usePolling } from '@optools/hooks/usePolling';
import { SWITCH_PROGRAM_SOURCE } from '@optools/services/operator-gateway/btc/mutations';
import { BTC_GAME_PIPELINE } from '@optools/services/operator-gateway/btc/queries';
import { ProgramSource, Region } from '@optools/services/operator-gateway/types/generated/graphql';
import { useRef } from 'react';
import { toast } from 'sonner';

export type UseProgramSourceSwitchOptions = {
  date: string;
  gamePk: number;
  mediaId: string;
  pipelineId: number;
  programSource: ProgramSource;
};

export function useProgramSourceSwitch({
  date,
  gamePk,
  mediaId,
  pipelineId,
  programSource,
}: UseProgramSourceSwitchOptions) {
  const programSourceRef = useRef(programSource);
  programSourceRef.current = programSource;

  const pendingProgramSourceRef = useRef(programSource);

  const [switchProgramSource, { loading: switchProgramSourceLoading }] =
    useMutation(SWITCH_PROGRAM_SOURCE);

  const { isPolling, startPolling } = usePolling({
    interval: 2000,
    onMaxAttemptsReached: () => {
      toast.warning('Failed to synchronize active source', {
        description:
          'Please refresh the page to ensure that the active source has been updated correctly.',
        duration: Infinity,
      });
      pendingProgramSourceRef.current = programSourceRef.current;
    },
    onPollingCompleted: () => {
      toast.success('Program Source Success', {
        description: `Updated Program Source to ${pendingProgramSourceRef.current}`,
      });
    },
    query: BTC_GAME_PIPELINE,
    shouldStopPolling: ({ game }) => {
      const nextPipeline = game?.pipelines.find((p) => p.id === pipelineId);
      return nextPipeline?.programSource === pendingProgramSourceRef.current;
    },
    variables: { gamePk, scheduleDate: date },
  });

  const handleSwitchProgramSource = (checked: boolean, region?: Region) => {
    const newProgramSource = checked ? ProgramSource.BACKHAUL : ProgramSource.FRONTHAUL;
    pendingProgramSourceRef.current = newProgramSource;

    switchProgramSource({
      onCompleted: ({ switchProgramSource: results }) => {
        const [result] = results;

        if (!result.success) {
          toast.error('Program Source Error', { description: result.reason });
          pendingProgramSourceRef.current = programSourceRef.current;
          return;
        }

        startPolling();
      },
      variables: { mediaIds: [mediaId], programSource: newProgramSource, region },
    });
  };

  return {
    handleSwitchProgramSource,
    isProgramSourcePending: switchProgramSourceLoading || isPolling,
  };
}
